import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import PDFImporter from '../../components/PDFImporter';
import PlanoAlimentarPreview from '../../components/PlanoAlimentarPreview';
import { refeicaoVazia, opcaoVazia, itemVazio, substituicaoVazia, normalizarRefeicoes, gerarListaDeCompras, gerarReceitas } from '../../lib/dietaUtils';
import { gerarPdfPlanoAlimentar, gerarPdfReceitas } from '../../lib/pdfGenerator';
import '../../styles/adminForms.css';

const aguaVazia = () => ({ instrucao: '', meta: '' });
const suplementoVazio = () => ({ nome: '', dose: '', momento: '' });

function estadoInicial(dietaEditando, alunaIdParam, alunas) {
  if (dietaEditando) {
    return {
      ...dietaEditando,
      titulo: dietaEditando.titulo || '',
      descricao: dietaEditando.descricao || '',
      calorias: dietaEditando.calorias || '',
      proteina: dietaEditando.proteina || '',
      obs: dietaEditando.obs || '',
      orientacoes: dietaEditando.orientacoes?.length > 0 ? dietaEditando.orientacoes : [''],
      refeicoes: normalizarRefeicoes(dietaEditando.refeicoes),
      agua: { instrucao: dietaEditando.agua?.instrucao || '', meta: dietaEditando.agua?.meta || '' },
      suplementacao: dietaEditando.suplementacao || [],
      listaCompras: dietaEditando.listaCompras?.length > 0 ? dietaEditando.listaCompras : gerarListaDeCompras(normalizarRefeicoes(dietaEditando.refeicoes)),
    };
  }
  return {
    alunaId: alunaIdParam || (alunas[0]?.id || ''),
    titulo: '', descricao: '', calorias: '', proteina: '', obs: '',
    orientacoes: [''],
    agua: aguaVazia(),
    refeicoes: [refeicaoVazia()],
    suplementacao: [],
    listaCompras: [],
  };
}

export default function CadastrarDieta() {
  const { salvarDieta, dietas, deletarDieta } = useData();
  const { alunas } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alunaIdParam = searchParams.get('aluna');
  const [searchParamsEdit] = useSearchParams();
  const editandoId = searchParamsEdit.get('editar');
  const dietaEditando = editandoId ? dietas.find(d => d.id === editandoId) : null;
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(() => estadoInicial(dietaEditando, alunaIdParam, alunas));
  const [novoItemCompra, setNovoItemCompra] = useState('');

  useEffect(() => {
    if (dietaEditando) setForm(estadoInicial(dietaEditando, alunaIdParam, alunas));
    else if (alunaIdParam) setForm(f => ({ ...f, alunaId: alunaIdParam }));
  }, [alunaIdParam, editandoId]);

  const alunaSel = alunas.find(a => a.id === form.alunaId);
  const upField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const upAgua = (k, v) => setForm(f => ({ ...f, agua: { ...f.agua, [k]: v } }));

  // --- refeições / opções / itens / substituições (atualização imutável em cascata) ---
  const withRefeicao = (ri, updater) => setForm(f => {
    const refeicoes = [...f.refeicoes];
    refeicoes[ri] = updater({ ...refeicoes[ri] });
    return { ...f, refeicoes };
  });
  const withOpcao = (ri, oi, updater) => withRefeicao(ri, ref => {
    const opcoes = [...ref.opcoes];
    opcoes[oi] = updater({ ...opcoes[oi] });
    return { ...ref, opcoes };
  });
  const withItem = (ri, oi, ii, updater) => withOpcao(ri, oi, op => {
    const itens = [...op.itens];
    itens[ii] = updater({ ...itens[ii] });
    return { ...op, itens };
  });

  const upRef = (ri, k, v) => withRefeicao(ri, ref => ({ ...ref, [k]: v }));
  const addRef = () => setForm(f => ({ ...f, refeicoes: [...f.refeicoes, refeicaoVazia()] }));
  const remRef = (ri) => setForm(f => ({ ...f, refeicoes: f.refeicoes.filter((_, x) => x !== ri) }));

  const addOpcao = (ri) => withRefeicao(ri, ref => ({ ...ref, opcoes: [...ref.opcoes, { ...opcaoVazia(), titulo: `Opção ${String(ref.opcoes.length + 1).padStart(2, '0')}` }] }));
  const remOpcao = (ri, oi) => withRefeicao(ri, ref => ({ ...ref, opcoes: ref.opcoes.filter((_, x) => x !== oi) }));
  const upOpcaoTitulo = (ri, oi, v) => withOpcao(ri, oi, op => ({ ...op, titulo: v }));
  const upOpcaoReceita = (ri, oi, v) => withOpcao(ri, oi, op => ({ ...op, receita: v }));

  const addItem = (ri, oi) => withOpcao(ri, oi, op => ({ ...op, itens: [...op.itens, itemVazio()] }));
  const remItem = (ri, oi, ii) => withOpcao(ri, oi, op => ({ ...op, itens: op.itens.filter((_, x) => x !== ii) }));
  const upItem = (ri, oi, ii, k, v) => withItem(ri, oi, ii, item => ({ ...item, [k]: v }));

  const addSub = (ri, oi, ii) => withItem(ri, oi, ii, item => ({ ...item, substituicoes: [...item.substituicoes, substituicaoVazia()] }));
  const remSub = (ri, oi, ii, si) => withItem(ri, oi, ii, item => ({ ...item, substituicoes: item.substituicoes.filter((_, x) => x !== si) }));
  const upSub = (ri, oi, ii, si, k, v) => withItem(ri, oi, ii, item => {
    const substituicoes = [...item.substituicoes];
    substituicoes[si] = { ...substituicoes[si], [k]: v };
    return { ...item, substituicoes };
  });

  // --- orientações (bullets) ---
  const upOrientacao = (i, v) => setForm(f => { const o = [...f.orientacoes]; o[i] = v; return { ...f, orientacoes: o }; });
  const addOrientacao = () => setForm(f => ({ ...f, orientacoes: [...f.orientacoes, ''] }));
  const remOrientacao = (i) => setForm(f => ({ ...f, orientacoes: f.orientacoes.filter((_, x) => x !== i) }));

  // --- suplementação ---
  const upSupl = (i, k, v) => { const s = [...form.suplementacao]; s[i] = { ...s[i], [k]: v }; setForm(f => ({ ...f, suplementacao: s })); };
  const addSupl = () => setForm(f => ({ ...f, suplementacao: [...f.suplementacao, suplementoVazio()] }));
  const remSupl = (i) => setForm(f => ({ ...f, suplementacao: f.suplementacao.filter((_, x) => x !== i) }));

  // --- lista de compras ---
  const remItemCompra = (i) => setForm(f => ({ ...f, listaCompras: f.listaCompras.filter((_, x) => x !== i) }));
  const addItemCompra = () => {
    const nome = novoItemCompra.trim();
    if (!nome) return;
    setForm(f => f.listaCompras.some(n => n.toLowerCase() === nome.toLowerCase()) ? f : ({ ...f, listaCompras: [...f.listaCompras, nome] }));
    setNovoItemCompra('');
  };
  const sincronizarListaCompras = () => setForm(f => {
    const derivados = gerarListaDeCompras(f.refeicoes);
    const faltando = derivados.filter(n => !f.listaCompras.some(m => m.toLowerCase() === n.toLowerCase()));
    return faltando.length > 0 ? { ...f, listaCompras: [...f.listaCompras, ...faltando].sort((a, b) => a.localeCompare(b, 'pt-BR')) } : f;
  });

  function handlePDFImport(dados) {
    setForm(f => {
      const refeicoes = dados.refeicoes?.length > 0 ? normalizarRefeicoes(dados.refeicoes) : f.refeicoes;
      return {
        ...f,
        titulo: dados.titulo || f.titulo,
        descricao: dados.descricao || f.descricao,
        calorias: dados.calorias || f.calorias,
        proteina: dados.proteina || f.proteina,
        agua: dados.agua ? { instrucao: dados.agua.instrucao || '', meta: dados.agua.meta || '' } : f.agua,
        orientacoes: Array.isArray(dados.orientacoes) && dados.orientacoes.length > 0 ? dados.orientacoes : f.orientacoes,
        refeicoes,
        suplementacao: Array.isArray(dados.suplementacao) && dados.suplementacao.length > 0 ? dados.suplementacao : f.suplementacao,
        listaCompras: dados.refeicoes?.length > 0 ? gerarListaDeCompras(refeicoes) : f.listaCompras,
      };
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    salvarDieta({ ...form, orientacoes: form.orientacoes.filter(o => o.trim()) });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    setForm(f => ({ ...f, titulo: '', descricao: '', calorias: '', proteina: '', orientacoes: [''], agua: aguaVazia(), refeicoes: [refeicaoVazia()], suplementacao: [], listaCompras: [] }));
  }

  const dietasDaAluna = dietas.filter(d => d.alunaId === form.alunaId).slice().reverse();

  return (
    <AdminLayout>
      <div className="af-page-header">
        <h1 className="af-title">{dietaEditando ? 'Editar' : 'Cadastrar'} <em>Plano Alimentar</em></h1>
        <button className="af-back-btn" onClick={() => alunaIdParam ? navigate('/admin/alunas') : navigate('/admin')}>← Voltar</button>
      </div>
      {alunaSel && <div className="af-aluna-chip"><span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--text)', color: '#FAF7F2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px' }}>{alunaSel.name.charAt(0)}</span>{alunaSel.name}</div>}
      <PDFImporter tipo="dieta" onImport={handlePDFImport} />
      {saved && <div className="af-success">✓ Plano alimentar salvo com sucesso!</div>}
      <form className="af-form" onSubmit={handleSubmit}>
        <div className="af-card">
          <div className="af-card-title">Informações gerais</div>
          <div className="af-row">
            <div className="af-field"><label className="af-label">Aluna</label><select className="af-select" value={form.alunaId} onChange={e => upField('alunaId', e.target.value)}>{alunas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
            <div className="af-field"><label className="af-label">Título do plano</label><input className="af-input" value={form.titulo} onChange={e => upField('titulo', e.target.value)} placeholder="Ex: Plano Semana 1" required /></div>
          </div>
          <div className="af-field"><label className="af-label">Descrição / objetivo</label><textarea className="af-textarea" value={form.descricao} onChange={e => upField('descricao', e.target.value)} placeholder="Objetivo do plano..." /></div>
          <div className="af-row">
            <div className="af-field"><label className="af-label">Total de calorias</label><input className="af-input" value={form.calorias} onChange={e => upField('calorias', e.target.value)} placeholder="Ex: 1.800 kcal" /></div>
            <div className="af-field"><label className="af-label">Proteína diária</label><input className="af-input" value={form.proteina} onChange={e => upField('proteina', e.target.value)} placeholder="Ex: 140g" /></div>
          </div>
          <div className="af-field" style={{ marginTop: '14px' }}><label className="af-label">Observações para a aluna</label><textarea className="af-textarea" value={form.obs || ''} onChange={e => upField('obs', e.target.value)} placeholder="Recado da equipe para a aluna sobre este plano (aparece no app)..." style={{ minHeight: '64px' }} /></div>
        </div>

        <div className="af-card">
          <div className="af-card-title">Orientações</div>
          <div style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '12px', lineHeight: 1.5 }}>
            Dica: para preencher automaticamente, use o botão "↑ Importar PDF" no topo da página — ele aceita tanto o plano completo quanto um PDF só com as orientações.
          </div>
          {form.orientacoes.map((o, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input className="af-input" value={o} onChange={e => upOrientacao(i, e.target.value)} placeholder="Ex: Priorizar carnes magras na maior parte da semana." />
              <button type="button" className="af-mini-remove" onClick={() => remOrientacao(i)}>✕</button>
            </div>
          ))}
          <button type="button" className="af-add-mini-btn" onClick={addOrientacao}>+ orientação</button>
        </div>

        <div className="af-card">
          <div className="af-card-title">Água</div>
          <div className="af-row">
            <div className="af-field"><label className="af-label">Instrução</label><textarea className="af-textarea" value={form.agua.instrucao} onChange={e => upAgua('instrucao', e.target.value)} placeholder="Ex: Iniciar o dia com 350ml em jejum..." style={{ minHeight: '56px' }} /></div>
            <div className="af-field"><label className="af-label">Meta diária</label><input className="af-input" value={form.agua.meta} onChange={e => upAgua('meta', e.target.value)} placeholder="Ex: 3114 ml" /></div>
          </div>
        </div>

        <div className="af-card">
          <div className="af-card-title">Refeições ({form.refeicoes.length})</div>
          {form.refeicoes.map((ref, ri) => (
            <div key={ref.id} className="af-sub-card">
              <div className="af-sub-num">{String(ri + 1).padStart(2, '0')}</div>
              {form.refeicoes.length > 1 && <button type="button" className="af-remove-btn" onClick={() => remRef(ri)}>✕</button>}
              <div className="af-row">
                <div className="af-field"><label className="af-label">Nome da refeição</label><input className="af-input" value={ref.nome} onChange={e => upRef(ri, 'nome', e.target.value)} placeholder="Ex: Almoço" required /></div>
                <div className="af-field"><label className="af-label">Horário sugerido</label><input className="af-input" type="time" value={ref.horario} onChange={e => upRef(ri, 'horario', e.target.value)} /></div>
              </div>
              <div className="af-field"><label className="af-label">Observação da refeição</label><textarea className="af-textarea" value={ref.observacao || ''} onChange={e => upRef(ri, 'observacao', e.target.value)} placeholder="Ex: consumir peixe pelo menos 3x por semana, ou receita da opção alternativa..." style={{ minHeight: '48px' }} /></div>

              {ref.opcoes.map((op, oi) => (
                <div key={oi} className={ref.opcoes.length > 1 ? 'af-opcao-block' : ''}>
                  {ref.opcoes.length > 1 && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <input className="af-input" style={{ flex: 1 }} value={op.titulo} onChange={e => upOpcaoTitulo(ri, oi, e.target.value)} placeholder="Ex: Opção 01" />
                      <button type="button" className="af-mini-remove" onClick={() => remOpcao(ri, oi)}>✕</button>
                    </div>
                  )}
                  <div className="af-sub-label">Alimentos</div>
                  {op.itens.map((item, ii) => (
                    <div key={item.id}>
                      <div className="af-item-row">
                        <input className="af-input" value={item.nome} onChange={e => upItem(ri, oi, ii, 'nome', e.target.value)} placeholder="Alimento" required />
                        <input className="af-input" value={item.quantidade} onChange={e => upItem(ri, oi, ii, 'quantidade', e.target.value)} placeholder="Qtd. (ex: 200)" />
                        <input className="af-input" value={item.unidade} onChange={e => upItem(ri, oi, ii, 'unidade', e.target.value)} placeholder="Unidade (ex: grama(s) (200g))" />
                        <button type="button" className="af-mini-remove" onClick={() => remItem(ri, oi, ii)}>✕</button>
                      </div>
                      <input className="af-input" style={{ marginBottom: '6px' }} value={item.observacao} onChange={e => upItem(ri, oi, ii, 'observacao', e.target.value)} placeholder="Observação do item (opcional)" />
                      {item.substituicoes.length > 0 && (
                        <div className="af-nested">
                          {item.substituicoes.map((sub, si) => (
                            <div key={sub.id} className="af-item-row">
                              <input className="af-input" value={sub.nome} onChange={e => upSub(ri, oi, ii, si, 'nome', e.target.value)} placeholder="Substituto" />
                              <input className="af-input" value={sub.quantidade} onChange={e => upSub(ri, oi, ii, si, 'quantidade', e.target.value)} placeholder="Qtd." />
                              <input className="af-input" value={sub.unidade} onChange={e => upSub(ri, oi, ii, si, 'unidade', e.target.value)} placeholder="Unidade" />
                              <button type="button" className="af-mini-remove" onClick={() => remSub(ri, oi, ii, si)}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button type="button" className="af-add-mini-btn" style={{ marginBottom: '10px' }} onClick={() => addSub(ri, oi, ii)}>+ substituição</button>
                    </div>
                  ))}
                  <button type="button" className="af-add-mini-btn" onClick={() => addItem(ri, oi)}>+ alimento</button>
                  <div className="af-field" style={{ marginTop: '10px' }}><label className="af-label">Receita (modo de preparo)</label><textarea className="af-textarea" value={op.receita || ''} onChange={e => upOpcaoReceita(ri, oi, e.target.value)} placeholder="Ex: Refogue a cebola, adicione o frango desfiado..." style={{ minHeight: '56px' }} /></div>
                </div>
              ))}
              <button type="button" className="af-add-mini-btn" style={{ marginTop: '10px' }} onClick={() => addOpcao(ri)}>+ opção alternativa (ex: Opção 02)</button>
            </div>
          ))}
          <button type="button" className="af-add-btn" onClick={addRef}>+ Adicionar refeição</button>
        </div>

        <div className="af-card">
          <div className="af-card-title">Suplementação</div>
          {form.suplementacao.map((s, i) => (
            <div key={i} className="af-item-row">
              <input className="af-input" value={s.nome} onChange={e => upSupl(i, 'nome', e.target.value)} placeholder="Suplemento (ex: Creatina)" />
              <input className="af-input" value={s.dose} onChange={e => upSupl(i, 'dose', e.target.value)} placeholder="Dose (ex: 5g)" />
              <input className="af-input" value={s.momento} onChange={e => upSupl(i, 'momento', e.target.value)} placeholder="Momento (ex: após o desjejum)" />
              <button type="button" className="af-mini-remove" onClick={() => remSupl(i)}>✕</button>
            </div>
          ))}
          <button type="button" className="af-add-btn" onClick={addSupl}>+ Adicionar suplemento</button>
        </div>

        <div className="af-card">
          <div className="af-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Lista de compras ({form.listaCompras.length})</span>
            <button type="button" className="af-add-mini-btn" onClick={sincronizarListaCompras}>↻ Atualizar a partir dos alimentos</button>
          </div>
          {form.listaCompras.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {form.listaCompras.map((nome, i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text)', background: 'var(--surface-2)', padding: '6px 6px 6px 12px', borderRadius: '100px' }}>
                  {nome}
                  <button type="button" onClick={() => remItemCompra(i)} style={{ background: 'transparent', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: '12px', padding: '2px' }}>✕</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input className="af-input" value={novoItemCompra} onChange={e => setNovoItemCompra(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItemCompra(); } }} placeholder="Adicionar item manualmente (ex: Papel toalha)" />
            <button type="button" className="af-add-mini-btn" onClick={addItemCompra}>+ item</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="submit" className="af-submit-btn">{dietaEditando ? 'Salvar alterações' : 'Salvar plano alimentar'}</button>
          <button type="button" className="af-submit-btn" style={{ background: 'transparent', color: 'var(--accent)', border: '1.5px solid var(--accent)' }} onClick={() => setPreview(form)}>👁 Pré-visualizar como a aluna</button>
        </div>
      </form>
      {dietasDaAluna.length > 0 && (
        <div style={{ marginTop: '32px', maxWidth: '720px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '12px' }}>Planos de {alunaSel?.name}</div>
          {dietasDaAluna.map(d => (
            <div key={d.id} className="af-existing-item">
              <div>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{d.titulo}{d.id === dietasDaAluna[0]?.id && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Atual</span>}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-2)' }}>{d.refeicoes?.length || 0} refeições · {d.calorias}{d.criadoEm && ` · cadastrado em ${new Date(d.criadoEm).toLocaleDateString('pt-BR')}`}</div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="af-del-btn" style={{ color: 'var(--accent)', borderColor: 'rgba(137,91,64,0.3)', border: '1px solid', padding: '4px 10px', borderRadius: 'var(--r-btn)' }} onClick={() => setPreview(d)}>👁 Prévia</button>
                <button className="af-del-btn" style={{ color: 'var(--accent)', borderColor: 'rgba(137,91,64,0.3)', border: '1px solid', padding: '4px 10px', borderRadius: 'var(--r-btn)' }} onClick={() => gerarPdfPlanoAlimentar(d, normalizarRefeicoes(d.refeicoes), alunaSel?.name || '')}>⬇ PDF</button>
                {gerarReceitas(normalizarRefeicoes(d.refeicoes)).length > 0 && (
                  <button className="af-del-btn" style={{ color: 'var(--accent)', borderColor: 'rgba(137,91,64,0.3)', border: '1px solid', padding: '4px 10px', borderRadius: 'var(--r-btn)' }} onClick={() => gerarPdfReceitas(gerarReceitas(normalizarRefeicoes(d.refeicoes)), d.titulo, alunaSel?.name || '')}>⬇ Receitas</button>
                )}
                <button className="af-del-btn" style={{ color: 'var(--accent)', borderColor: 'rgba(137,91,64,0.3)', border: '1px solid', padding: '4px 10px', borderRadius: 'var(--r-btn)' }} onClick={() => navigate(`/admin/dieta/nova?aluna=${d.alunaId}&editar=${d.id}`)}>Editar</button>
                <button className="af-del-btn" onClick={() => deletarDieta(d.id)}>Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(37,28,25,0.65)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 300, padding: '24px', overflowY: 'auto' }} onClick={e => { if (e.target === e.currentTarget) setPreview(null); }}>
          <div style={{ background: 'var(--bg-app, #FAF7F2)', width: '100%', maxWidth: '480px', borderRadius: 'var(--r-card)', padding: '20px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)' }}>Como a aluna vê</div>
              <button type="button" onClick={() => setPreview(null)} style={{ background: 'transparent', border: 'none', fontSize: '20px', color: 'var(--text-2)', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <PlanoAlimentarPreview dieta={preview} lang="pt" alunaNome={alunaSel?.name} />
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
